/* istanbul ignore file */
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import {
  CellApiModule,
  CellStyleModule,
  CheckboxEditorModule,
  ClientSideRowModelApiModule,
  ClientSideRowModelModule,
  ColumnApiModule,
  CsvExportModule,
  CustomEditorModule,
  CustomFilterModule,
  DateEditorModule,
  DateFilterModule,
  EventApiModule,
  ExternalFilterModule,
  GridStateModule,
  HighlightChangesModule,
  LargeTextEditorModule,
  ModuleRegistry,
  NumberEditorModule,
  NumberFilterModule,
  PinnedRowModule,
  QuickFilterModule,
  RowApiModule,
  RowAutoHeightModule,
  RowDragModule,
  RowSelectionModule,
  RowStyleModule,
  ScrollApiModule,
  SelectEditorModule,
  TextEditorModule,
  TextFilterModule,
  UndoRedoEditModule,
  ValidationModule,
  ValueCacheModule,
} from 'ag-grid-community';
import {
  AdvancedFilterModule,
  BatchEditModule,
  CellSelectionModule,
  ColumnMenuModule,
  ColumnsToolPanelModule,
  FiltersToolPanelModule,
  LicenseManager,
  MultiFilterModule,
  NewFiltersToolPanelModule,
  RichSelectModule,
  RowGroupingModule,
  RowNumbersModule,
  ServerSideRowModelApiModule,
  ServerSideRowModelModule, // required for rowModelType='serverSide'
  SetFilterModule,
  SideBarModule,
} from 'ag-grid-enterprise';

import React from 'react';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import { PersistGate } from 'redux-persist/integration/react';

import UnderMaintenance from 'common/components/UnderMaintenance';
import SnackbarProvider from 'common/theme/SnackbarProvider';
import ThemeProvider from 'common/theme/ThemeProvider';
import { configuredStore } from 'store';

import App from './App';
import * as serviceWorkerRegistration from './serviceWorkerRegistration';

const { store, persistor } = configuredStore;
const root = createRoot(document.getElementById('root'));

ModuleRegistry.registerModules([
  PinnedRowModule,
  RowAutoHeightModule,
  RowStyleModule,
  RowDragModule,
  CellStyleModule,
  HighlightChangesModule,
  TextFilterModule,
  NumberFilterModule,
  DateFilterModule,
  CustomFilterModule,
  ExternalFilterModule,
  QuickFilterModule,
  RowSelectionModule,
  TextEditorModule,
  LargeTextEditorModule,
  SelectEditorModule,
  NumberEditorModule,
  DateEditorModule,
  CheckboxEditorModule,
  CustomEditorModule,
  UndoRedoEditModule,
  ValueCacheModule,
  CsvExportModule,
  GridStateModule,
  ColumnApiModule,
  RowApiModule,
  CellApiModule,
  // api.addEventListener lives here; without it the call is refused at runtime rather than
  // failing to compile, so a listener silently never fires.
  EventApiModule,
  // api.ensureNodeVisible / ensureIndexVisible / ensureColumnVisible live here; without it those
  // calls are refused the same way, so nothing scrolls.
  ScrollApiModule,
  ClientSideRowModelApiModule,
  ClientSideRowModelModule,
  RowNumbersModule,
  SetFilterModule,
  MultiFilterModule,
  AdvancedFilterModule,
  CellSelectionModule,
  RichSelectModule,
  BatchEditModule,
  ValidationModule,
  SideBarModule,
  RowGroupingModule,
  ServerSideRowModelModule,
  ServerSideRowModelApiModule,
  NewFiltersToolPanelModule,
  FiltersToolPanelModule,
  ColumnsToolPanelModule,
  ColumnMenuModule,
  ...(process.env.NODE_ENV === 'development' ? [ValidationModule] : []),
]);

LicenseManager.setLicenseKey(process.env.REACT_APP_AG_GRID_KEY);

// if (process.env.NODE_ENV === 'development') {
//   // eslint-disable-next-line global-require
//   const { worker } = require('./mocks/browser');
//   worker.start({ onUnhandledRequest: 'bypass' });
// }

root.render(
  <React.StrictMode>
    <Provider store={store}>
      <PersistGate loading={null} persistor={persistor}>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <ThemeProvider>
            <SnackbarProvider>
              {process.env.REACT_APP_MAINTENANCE ? <UnderMaintenance /> : <App />}
            </SnackbarProvider>
          </ThemeProvider>
        </LocalizationProvider>
      </PersistGate>
    </Provider>
  </React.StrictMode>
);

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://cra.link/PWA
serviceWorkerRegistration.unregister();
